import React, {FormEvent, useContext, useEffect, useState} from 'react';
import {CreateHourRecord, CreateHoursNumber, ListAllToAddHoursRes} from 'types';
import {Modal, Button} from "react-bootstrap";
import {apiUrl} from "../../config/api";
import {Spinner} from "../../component/common/spiner/spinner";
import { toast } from "react-toastify";
import {AuthContextUser} from "../../auth/AuthContext";

interface EditHoursProps {
    show: boolean;
    onHide: () => void;
    hourData: any;
    onHoursChange: () => void;
}

export const EditHours = (props: EditHoursProps) => {
    const { user } = useContext(AuthContextUser);

    const [data, setData] = useState<ListAllToAddHoursRes >({
        employeeList: [],
        kindofworkList: [],
        projectList: [],
    });

    const [hourForForm, setHourForForm] = useState<CreateHoursNumber>({
        minutes: 0,
        hours: 0,
    });

    const [form, setForm] = useState<CreateHourRecord>({
        projectId: '',
        employeeId: '',
        kindofworkId: '',
        quantity: 0,
        date: '',
    });

    const [loading, setLoading] = useState<boolean>(false);

    const updateForm = (key: string, value: any) => {
        setForm(form => ({
            ...form,
            [key]:  (key === 'quantity') ? Number(value) : value,
        }));
    };

    const updateQuantityFromTime = (key: string, value: any) => {
        setHourForForm(hourForForm => {
            const updatedHourForForm = {
                ...hourForForm,
                [key]: Number(value),
            };

            const totalHours = (updatedHourForForm.hours + updatedHourForForm.minutes / 60).toFixed(2);
            updateForm('quantity', totalHours);
            return updatedHourForForm;
        });
    };

    const refreshHours = async () => {
        try {
            const apiResponse = await fetch(`${apiUrl}/hour/add`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const result = await apiResponse.json();
            setData(result);

            // Ustaw formularz z danymi edytowanej godziny
            if (props.hourData) {
                const quantity = Number(props.hourData.quantity) || 0;
                const hours = Math.floor(quantity);
                const minutes = Math.round((quantity - hours) * 60);

                setForm({
                    projectId: props.hourData.projectId || result.projectList[0]?.id || '',
                    employeeId: props.hourData.employeeId || result.employeeList[0]?.id || '',
                    kindofworkId: props.hourData.kindofworkId || result.kindofworkList[0]?.id || '',
                    quantity: quantity,
                    date: props.hourData.date || new Date().toLocaleDateString('en-CA'),
                });

                setHourForForm({
                    hours: hours,
                    minutes: minutes,
                });
            }
        } catch (error) {
            toast.error(`Błąd podczas pobierania danych: ${error}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (props.show && props.hourData) {
            refreshHours();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.show, props.hourData]);

    const sendForm = async (e: FormEvent) => {
        e.preventDefault();

        const hourId = props.hourData.id || (props.hourData as any).hourId || (props.hourData as any)._id;
        
        if (!hourId) {
            toast.error('Brak ID godziny - nie można edytować');
            return;
        }

        setLoading(true);
        try {
            const apiResponse = await fetch(`${apiUrl}/hour/${hourId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(form),
            });

            if (!apiResponse.ok) {
                const errorText = await apiResponse.text();
                toast.error(`Błąd podczas edycji: ${errorText || apiResponse.statusText}`);
                return;
            }

            const result = await apiResponse.json();
            
            if (result.isSuccess) {
                toast.success("Godziny zostały zaktualizowane!");
                props.onHoursChange();
                props.onHide();
            } else {
                toast.error("Wystąpił problem podczas aktualizacji");
            }
        } catch (error) {
            toast.error(`Błąd: ${error}`);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Modal show={props.show} onHide={props.onHide} centered>
                <Modal.Body>
                    <Spinner/>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <Modal show={props.show} onHide={props.onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Edytuj Godziny pracy</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={sendForm}>
                    {user?.role === 'Boss' && (
                        <div className='LabelForm'>
                            Pracownik:
                            <select
                                className="InputForm"
                                value={form.employeeId}
                                onChange={e => updateForm('employeeId', e.target.value)}
                            >
                                {data.employeeList.map(employee => (
                                    <option key={employee.id} value={employee.id}>
                                        {employee.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    
                    <div className='LabelForm'>
                        <label>
                            Data wykonania:
                            <input
                                className="InputForm"
                                type="date"
                                name="date"
                                value={form.date}
                                onChange={e => updateForm('date', e.target.value)}
                            />
                        </label>
                    </div>

                    <div className='LabelForm'>
                        Projekt:
                        <select
                            className="InputForm"
                            value={form.projectId}
                            onChange={e => updateForm('projectId', e.target.value)}
                        >
                            {data.projectList.map(project => (
                                <option key={project.id} value={project.id}>
                                    {project.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className='LabelForm'>
                        Rodzaj:
                        <select
                            className="InputForm"
                            value={form.kindofworkId}
                            onChange={e => updateForm('kindofworkId', e.target.value)}
                        >
                            {data.kindofworkList.map(k => (
                                <option key={k.id} value={k.id}>
                                    {k.hourstype}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className='LabelForm'>
                        <div>Ilość godzin:</div>
                        <div className='HourInput'>
                            <label>
                                <div>
                                    <input
                                        className="InputForm"
                                        type="number"
                                        name="hours"
                                        min="0"
                                        step="1"
                                        title="Wprowadź liczbę godzin"
                                        value={hourForForm.hours}
                                        onChange={e => updateQuantityFromTime('hours', e.target.value)}
                                        style={{ width: '20%', marginRight: '40%'}}
                                    />
                                </div>
                                <div>
                                    <select
                                        className="InputForm"
                                        name="minutes"
                                        value={hourForForm.minutes}
                                        onChange={e => updateQuantityFromTime("minutes", Number(e.target.value))}
                                        style={{ width: '30%'}}
                                    >
                                        <option value={0}>0 minut</option>
                                        <option value={15}>15 minut</option>
                                        <option value={30}>30 minut</option>
                                        <option value={45}>45 minut</option>
                                    </select>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button variant="secondary" onClick={props.onHide}>
                            Anuluj
                        </Button>
                        <Button variant="primary" type="submit">
                            Zapisz zmiany
                        </Button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};